import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useWeb3React } from "@web3-react/core";

import Modal from "../../components/Modal/Modal";
import Checkbox from "../../components/Checkbox/Checkbox";
import Tooltip from "../../components/Tooltip/Tooltip";
import Footer from "../../Footer";
import earnedBigIcon from "../../img/earnlogo.svg";
import Vault from "../../abis/Vault.json";
import Reader from "../../abis/Reader.json";
import Vester from "../../abis/Vester.json";
import RewardRouter from "../../abis/RewardRouter.json";
import RewardReader from "../../abis/RewardReader.json";
import Token from "../../abis/Token.json";
import EquityManager from "../../abis/EquityManager.json";

import { ethers } from "ethers";
import {
  helperToast,
  bigNumberify,
  fetcher,
  formatAmount,
  formatKeyAmount,
  formatAmountFree,
  getChainName,
  expandDecimals,
  parseValue,
  approveTokens,
  getServerUrl,
  useLocalStorageSerializeKey,
  useChainId,
  EQUITY_DECIMALS,
  USD_DECIMALS,
  EQUALEQUITY_DISPLAY_DECIMALS,
  BASIS_POINTS_DIVISOR,
  FTM,
  PLACEHOLDER_ACCOUNT,
  getBalanceAndSupplyData,
  getDepositBalanceData,
  getVestingData,
  getStakingData,
  getProcessedData,
  getPageTitle,
} from "../../Helpers";
import { callContract, useEqualPrice, useTotalEqualStaked, useTotalEqualSupply, useTotalEqualExcludedFromSupply } from "../../Api";
import { getConstant } from "../../Constants";

import useSWR from "swr";

import { getContract } from "../../Addresses";

import "./StakeV2.css";
import SEO from "../../components/Common/SEO";
import { BREAK } from "graphql";

const { AddressZero } = ethers.constants;

function StakeModal(props) {
  const {
    isVisible,
    setIsVisible,
    chainId,
    title,
    maxAmount,
    value,
    setValue,
    active,
    account,
    library,
    stakingTokenSymbol,
    stakingTokenAddress,
    farmAddress,
    rewardRouterAddress,
    stakeMethodName,
    setPendingTxns,
  } = props;
  const [isStaking, setIsStaking] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const { data: tokenAllowance } = useSWR(
    active && stakingTokenAddress && [active, chainId, stakingTokenAddress, "allowance", account, farmAddress],
    {
      fetcher: fetcher(library, Token),
    }
  );

  let amount = parseValue(value, 18);
  const needApproval = farmAddress !== AddressZero && tokenAllowance && amount && amount.gt(tokenAllowance);

  const getError = () => {
    if (!amount || amount.eq(0)) {
      return "ENTER AN AMOUNT";
    }
    if (maxAmount && amount.gt(maxAmount)) {
      return "MAX AMOUNT EXCEEDED";
    }
  };

  const onClickPrimary = () => {
    if (needApproval) {
      approveTokens({
        setIsApproving,
        library,
        tokenAddress: stakingTokenAddress,
        spender: farmAddress,
        chainId,
      });
      return;
    }

    setIsStaking(true);
    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter, library.getSigner());

    callContract(chainId, contract, stakeMethodName, [amount], {
      sentMsg: "Stake submitted!",
      failMsg: "Stake failed.",
      setPendingTxns,
    })
      .then(async (res) => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsStaking(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isApproving) {
      return false;
    }
    if (isStaking) {
      return false;
    }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isApproving) {
      return `Approving ${stakingTokenSymbol}...`;
    }
    if (needApproval) {
      return `Approve ${stakingTokenSymbol}`;
    }
    if (isStaking) {
      return "Staking...";
    }
    return "Stake";
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={title}>
        <div className="Exchange-swap-section">
          <div className="Exchange-swap-section-top">
            <div className="muted">
              <div className="Exchange-swap-usd">Stake</div>
            </div>
            <div className="muted align-right clickable" onClick={() => setValue(formatAmountFree(maxAmount, 18, 18))}>
              Max: {formatAmount(maxAmount, 18, 4, true)}
            </div>
          </div>
          <div className="Exchange-swap-section-bottom">
            <div>
              <input
                type="number"
                placeholder="0.0"
                className="Exchange-swap-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
            <div className="PositionEditor-token-symbol">{stakingTokenSymbol}</div>
          </div>
        </div>
        <div className="Exchange-swap-button-container">
          <button className="App-cta Exchange-swap-button" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
            {getPrimaryText()}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function UnstakeModal(props) {
  const {
    isVisible,
    setIsVisible,
    chainId,
    title,
    maxAmount,
    value,
    setValue,
    library,
    unstakingTokenSymbol,
    rewardRouterAddress,
    unstakeMethodName,
    multiplierPointsAmount,
    reservedAmount,
    bonusEqualInFeeEqual,
    setPendingTxns,
  } = props;
  const [isUnstaking, setIsUnstaking] = useState(false);

  let amount = parseValue(value, 18);
  let burnAmount;

  if (
    multiplierPointsAmount &&
    multiplierPointsAmount.gt(0) &&
    amount &&
    amount.gt(0) &&
    bonusEqualInFeeEqual &&
    bonusEqualInFeeEqual.gt(0)
  ) {
    burnAmount = multiplierPointsAmount.mul(amount).div(bonusEqualInFeeEqual);
  }

  const shouldShowReductionAmount = true;
  let rewardReductionBasisPoints;
  if (burnAmount && bonusEqualInFeeEqual) {
    rewardReductionBasisPoints = burnAmount.mul(BASIS_POINTS_DIVISOR).div(bonusEqualInFeeEqual);
  }

  const getError = () => {
    if (!amount) {
      return "ENTER AN AMOUNT";
    }
    if (amount.gt(maxAmount)) {
      return "MAX AMOUNT EXCEEDED";
    }
  };

  const onClickPrimary = () => {
    setIsUnstaking(true);
    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter, library.getSigner());
    callContract(chainId, contract, unstakeMethodName, [amount], {
      sentMsg: "Unstake submitted!",
      failMsg: "Unstake failed.",
      successMsg: "Unstake completed!",
      setPendingTxns,
    })
      .then(async (res) => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsUnstaking(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isUnstaking) {
      return false;
    }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isUnstaking) {
      return "Unstaking...";
    }
    return "Unstake";
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={title}>
        <div className="Exchange-swap-section">
          <div className="Exchange-swap-section-top">
            <div className="muted">
              <div className="Exchange-swap-usd">Unstake</div>
            </div>
            <div className="muted align-right clickable" onClick={() => setValue(formatAmountFree(maxAmount, 18, 18))}>
              Max: {formatAmount(maxAmount, 18, 4, true)}
            </div>
          </div>
          <div className="Exchange-swap-section-bottom">
            <div>
              <input
                type="number"
                placeholder="0.0"
                className="Exchange-swap-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
            <div className="PositionEditor-token-symbol">{unstakingTokenSymbol}</div>
          </div>
        </div>
        {reservedAmount && reservedAmount.gt(0) && (
          <div className="Modal-note">
            You have {formatAmount(reservedAmount, 18, 2, true)} tokens reserved for vesting.
          </div>
        )}
        {burnAmount && burnAmount.gt(0) && rewardReductionBasisPoints && rewardReductionBasisPoints.gt(0) && (
          <div className="Modal-note">
            Unstaking will burn&nbsp;
            <a href=" https://docs.equalizer.exchange/rewards" target="_blank" rel="noopener noreferrer">
              {formatAmount(burnAmount, 18, 4, true)} Multiplier Points
            </a>
            .&nbsp;
            {shouldShowReductionAmount && (
              <span>Boost %: -{formatAmount(rewardReductionBasisPoints, 2, 2)}%.</span>
            )}
          </div>
        )}
        <div className="Exchange-swap-button-container">
          <button className="App-cta Exchange-swap-button" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
            {getPrimaryText()}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function VesterDepositModal(props) {
  const {
    isVisible,
    setIsVisible,
    chainId,
    title,
    maxAmount,
    value,
    setValue,
    balance,
    escrowedBalance,
    averageStakedAmount,
    maxVestableAmount,
    library,
    stakeTokenLabel,
    reserveAmount,
    maxReserveAmount,
    vesterAddress,
    setPendingTxns,
  } = props;
  const [isDepositing, setIsDepositing] = useState(false);

  let amount = parseValue(value, 18);

  let nextReserveAmount = reserveAmount;

  let nextDepositAmount = escrowedBalance;
  if (amount) {
    nextDepositAmount = escrowedBalance.add(amount);
  }

  let additionalReserveAmount = bigNumberify(0);
  if (amount && averageStakedAmount && maxVestableAmount && maxVestableAmount.gt(0)) {
    nextReserveAmount = nextDepositAmount.mul(averageStakedAmount).div(maxVestableAmount);
    if (nextReserveAmount.gt(reserveAmount)) {
      additionalReserveAmount = nextReserveAmount.sub(reserveAmount);
    }
  }

  const getError = () => {
    if (!amount || amount.eq(0)) {
      return "ENTER AN AMOUNT";
    }
    if (maxAmount && amount.gt(maxAmount)) {
      return "MAX AMOUNT EXCEEDED";
    }
    if (nextReserveAmount.gt(maxReserveAmount)) {
      return "INSUFFICIENT STAKED TOKENS";
    }
  };

  const onClickPrimary = () => {
    setIsDepositing(true);
    const contract = new ethers.Contract(vesterAddress, Vester.abi, library.getSigner());

    callContract(chainId, contract, "deposit", [amount], {
      sentMsg: "Deposit submitted!",
      failMsg: "Deposit failed!",
      successMsg: "Deposited!",
      setPendingTxns,
    })
      .then(async (res) => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsDepositing(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isDepositing) {
      return false;
    }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isDepositing) {
      return "Depositing...";
    }
    return "Deposit";
  };

  return (
    <SEO title={getPageTitle("Earn")}>
      <div className="StakeModal">
        <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={title} className="non-scrollable">
          <div className="Exchange-swap-section">
            <div className="Exchange-swap-section-top">
              <div className="muted">
                <div className="Exchange-swap-usd">Deposit</div>
              </div>
              <div
                className="muted align-right clickable"
                onClick={() => setValue(formatAmountFree(maxAmount, 18, 18))}
              >
                Max: {formatAmount(maxAmount, 18, 4, true)}
              </div>
            </div>
            <div className="Exchange-swap-section-bottom">
              <div>
                <input
                  type="number"
                  placeholder="0.0"
                  className="Exchange-swap-input"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </div>
              <div className="PositionEditor-token-symbol">esEQUAL</div>
            </div>
          </div>
          <div className="VesterDepositModal-info-rows">
            <div className="Exchange-info-row">
              <div className="Exchange-info-label">Wallet</div>
              <div className="align-right">{formatAmount(balance, 18, 2, true)} esEQUAL</div>
            </div>
            <div className="Exchange-info-row">
              <div className="Exchange-info-label">Vault Capacity</div>
              <div className="align-right">
                <Tooltip
                  handle={`${formatAmount(nextDepositAmount, 18, 2, true)} / ${formatAmount(
                    maxVestableAmount,
                    18,
                    2,
                    true
                  )}`}
                  position="right-bottom"
                  renderContent={() => {
                    return (
                      <>
                        Vault Capacity for your Account
                        <br />
                        <br />
                        Deposited: {formatAmount(escrowedBalance, 18, 2, true)} esEQUAL
                        <br />
                        Max Capacity: {formatAmount(maxVestableAmount, 18, 2, true)} esEQUAL
                        <br />
                      </>
                    );
                  }}
                />
              </div>
            </div>
          </div>
          <div className="Exchange-info-row">
            <div className="Exchange-info-label">Reserve Amount</div>
            <div className="align-right">
              <Tooltip
                handle={`${formatAmount(
                  reserveAmount && reserveAmount.gte(additionalReserveAmount) ? reserveAmount : additionalReserveAmount,
                  18,
                  2,
                  true
                )} / ${formatAmount(maxReserveAmount, 18, 2, true)}`}
                position="right-bottom"
                renderContent={() => {
                  return (
                    <>
                      Current Reserved: {formatAmount(reserveAmount, 18, 2, true)}
                      <br />
                      Additional reserve required: {formatAmount(additionalReserveAmount, 18, 2, true)}
                      <br />
                      {amount && nextReserveAmount.gt(maxReserveAmount) && (
                        <div>
                          <br />
                          You need a total of at least {formatAmount(nextReserveAmount, 18, 2, true)} {stakeTokenLabel}{" "}
                          to vest {formatAmount(amount, 18, 2, true)} esEQUAL.
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>
          </div>
          <div className="Exchange-swap-button-container">
            <button className="App-cta Exchange-swap-button" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
              {getPrimaryText()}
            </button>
          </div>
        </Modal>
      </div>
    </SEO>
  );
}

function VesterWithdrawModal(props) {
  const { isVisible, setIsVisible, chainId, title, library, vesterAddress, setPendingTxns } = props;
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const onClickPrimary = () => {
    setIsWithdrawing(true);
    const contract = new ethers.Contract(vesterAddress, Vester.abi, library.getSigner());

    callContract(chainId, contract, "withdraw", [], {
      sentMsg: "Withdraw submitted.",
      failMsg: "Withdraw failed.",
      successMsg: "Withdrawn!",
      setPendingTxns,
    })
      .then(async (res) => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsWithdrawing(false);
      });
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label={title}>
        <div>
        This will halt vesting and retrieve all tokens from reserve.
          <br />
          <br />
          Unvested EQUAL tokens will stay as EQUAL tokens.
          <br />
          <br />
          For claiming unvested EQUAL tokens without withdrawing, use the "CLAIM" button under the "Total Earnings" section.
          <br />
          <br />
        </div>
        <div className="Exchange-swap-button-container">
          <button className="App-cta Exchange-swap-button" onClick={onClickPrimary} disabled={isWithdrawing}>
            {!isWithdrawing && "Confirm Withdraw"}
            {isWithdrawing && "Confirming..."}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function CompoundModal(props) {
  const {
    isVisible,
    setIsVisible,
    rewardRouterAddress,
    active,
    account,
    library,
    chainId,
    setPendingTxns,
    totalVesterRewards,
    nativeTokenSymbol,
    wrappedTokenSymbol,
  } = props;
  const [isCompounding, setIsCompounding] = useState(false);
  const [shouldClaimEqual, setShouldClaimEqual] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-equal"],
    true
  );
  const [shouldStakeEqual, setShouldStakeEqual] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-stake-equal"],
    true
  );
  const [shouldClaimEsEqual, setShouldClaimEsEqual] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-es-equal"],
    true
  );
  const [shouldStakeEsEqual, setShouldStakeEsEqual] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-stake-es-equal"],
    true
  );
  const [shouldStakeMultiplierPoints, setShouldStakeMultiplierPoints] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-stake-multiplier-points"],
    true
  );
  const [shouldClaimWeth, setShouldClaimWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-weth"],
    true
  );
  const [shouldConvertWeth, setShouldConvertWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-convert-weth"],
    true
  );

  const equalAddress = getContract(chainId, "EQUAL");
  const stakedEqualTrackerAddress = getContract(chainId, "StakedEqualTracker");

  const [isApproving, setIsApproving] = useState(false);

  const { data: tokenAllowance } = useSWR(
    active && [active, chainId, equalAddress, "allowance", account, stakedEqualTrackerAddress],
    {
      fetcher: fetcher(library, Token),
    }
  );

  const needApproval = shouldStakeEqual && tokenAllowance && totalVesterRewards && totalVesterRewards.gt(tokenAllowance);

  const isPrimaryEnabled = () => {
    return !isCompounding && !isApproving && !isCompounding;
  };

  const getPrimaryText = () => {
    if (isApproving) {
      return `Approving EQUAL...`;
    }
    if (needApproval) {
      return `Approve EQUAL`;
    }
    if (isCompounding) {
      return "Compounding...";
    }
    return "Compound";
  };

  const onClickPrimary = () => {
    if (needApproval) {
      approveTokens({
        setIsApproving,
        library,
        tokenAddress: equalAddress,
        spender: stakedEqualTrackerAddress,
        chainId,
      });
      return;
    }

    setIsCompounding(true);

    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter, library.getSigner());
    callContract(
      chainId,
      contract,
      "handleRewards",
      [
        shouldClaimEqual || shouldStakeEqual,
        shouldStakeEqual,
        shouldClaimEsEqual || shouldStakeEsEqual,
        shouldStakeEsEqual,
        shouldStakeMultiplierPoints,
        shouldClaimWeth || shouldConvertWeth,
        shouldConvertWeth,
      ],
      {
        sentMsg: "Compound submitted!",
        failMsg: "Compound failed.",
        successMsg: "Compound completed!",
        setPendingTxns,
      }
    )
      .then(async (res) => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsCompounding(false);
      });
  };

  const toggleShouldStakeEqual = (value) => {
    if (value) {
      setShouldClaimEqual(true);
    }
    setShouldStakeEqual(value);
  };

  const toggleShouldStakeEsEqual = (value) => {
    if (value) {
      setShouldClaimEsEqual(true);
    }
    setShouldStakeEsEqual(value);
  };

  const toggleConvertWeth = (value) => {
    if (value) {
      setShouldClaimWeth(true);
    }
    setShouldConvertWeth(value);
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label="Compound Rewards">
        <div className="CompoundModal-menu">
          <div>
            <Checkbox isChecked={shouldStakeMultiplierPoints} setIsChecked={setShouldStakeMultiplierPoints}>
              Stake Multiplier Points
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldClaimEqual} setIsChecked={setShouldClaimEqual} disabled={shouldStakeEqual}>
              Claim unvested EQUAL
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldStakeEqual} setIsChecked={toggleShouldStakeEqual}>
              Stake claimed EQUAL
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldClaimEsEqual} setIsChecked={setShouldClaimEsEqual} disabled={shouldStakeEsEqual}>
              Claim esEQUAL Rewards
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldStakeEsEqual} setIsChecked={toggleShouldStakeEsEqual}>
              Stake claimed esEQUAL
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldClaimWeth} setIsChecked={setShouldClaimWeth} disabled={shouldConvertWeth}>
              Claim {wrappedTokenSymbol} Rewards
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldConvertWeth} setIsChecked={toggleConvertWeth}>
              Convert {wrappedTokenSymbol} to {nativeTokenSymbol}
            </Checkbox>
          </div>
        </div>
        <div className="Exchange-swap-button-container">
          <button className="App-cta Exchange-swap-button text-uppercase" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
            {getPrimaryText()}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function ClaimModal(props) {
  const {
    isVisible,
    setIsVisible,
    rewardRouterAddress,
    library,
    chainId,
    setPendingTxns,
    nativeTokenSymbol,
    wrappedTokenSymbol,
  } = props;
  const [isClaiming, setIsClaiming] = useState(false);
  const [shouldClaimEqual, setShouldClaimEqual] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-claim-should-claim-equal"],
    true
  );
  const [shouldClaimEsEqual, setShouldClaimEsEqual] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-claim-should-claim-es-equal"],
    true
  );
  const [shouldClaimWeth, setShouldClaimWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-claim-should-claim-weth"],
    true
  );
  const [shouldConvertWeth, setShouldConvertWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-claim-should-convert-weth"],
    true
  );

  const isPrimaryEnabled = () => {
    return !isClaiming;
  };

  const getPrimaryText = () => {
    if (isClaiming) {
      return `CLAIMING...`;
    }
    return "CLAIM";
  };

  const onClickPrimary = () => {
    setIsClaiming(true);

    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter, library.getSigner());
    callContract(
      chainId,
      contract,
      "handleRewards",
      [
        shouldClaimEqual,
        false, // shouldStakeEqual
        shouldClaimEsEqual,
        false, // shouldStakeEsEqual
        false, // shouldStakeMultiplierPoints
        shouldClaimWeth,
        shouldConvertWeth,
      ],
      {
        sentMsg: "Claim submitted.",
        failMsg: "Claim failed.",
        successMsg: "Claim completed!",
        setPendingTxns,
      }
    )
      .then(async (res) => {
        setIsVisible(false);
      })
      .finally(() => {
        setIsClaiming(false);
      });
  };

  const toggleConvertWeth = (value) => {
    if (value) {
      setShouldClaimWeth(true);
    }
    setShouldConvertWeth(value);
  };

  return (
    <div className="StakeModal">
      <Modal isVisible={isVisible} setIsVisible={setIsVisible} label="Claim Rewards">
        <div className="CompoundModal-menu">
          <div>
            <Checkbox isChecked={shouldClaimEqual} setIsChecked={setShouldClaimEqual}>
              Claim unvested EQUAL
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldClaimEsEqual} setIsChecked={setShouldClaimEsEqual}>
              Claim esEQUAL Rewards
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldClaimWeth} setIsChecked={setShouldClaimWeth} disabled={shouldConvertWeth}>
              Claim {wrappedTokenSymbol} Rewards
            </Checkbox>
          </div>
          <div>
            <Checkbox isChecked={shouldConvertWeth} setIsChecked={toggleConvertWeth}>
              Convert {wrappedTokenSymbol} to {nativeTokenSymbol}
            </Checkbox>
          </div>
        </div>
        <div className="Exchange-swap-button-container">
          <button className="App-cta Exchange-swap-button" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
            {getPrimaryText()}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default function StakeV2({ setPendingTxns, connectWallet }) {
  const { active, library, account } = useWeb3React();
  const { chainId } = useChainId();

  const chainName = getChainName(chainId);

  const hasInsurance = false;

  const [isStakeModalVisible, setIsStakeModalVisible] = useState(false);
  const [stakeModalTitle, setStakeModalTitle] = useState("");
  const [stakeModalMaxAmount, setStakeModalMaxAmount] = useState(undefined);
  const [stakeValue, setStakeValue] = useState("");
  const [stakingTokenSymbol, setStakingTokenSymbol] = useState("");
  const [stakingTokenAddress, setStakingTokenAddress] = useState("");
  const [stakingFarmAddress, setStakingFarmAddress] = useState("");
  const [stakeMethodName, setStakeMethodName] = useState("");

  const [isUnstakeModalVisible, setIsUnstakeModalVisible] = useState(false);
  const [unstakeModalTitle, setUnstakeModalTitle] = useState("");
  const [unstakeModalMaxAmount, setUnstakeModalMaxAmount] = useState(undefined);
  const [unstakeModalReservedAmount, setUnstakeModalReservedAmount] = useState(undefined);
  const [unstakeValue, setUnstakeValue] = useState("");
  const [unstakingTokenSymbol, setUnstakingTokenSymbol] = useState("");
  const [unstakeMethodName, setUnstakeMethodName] = useState("");

  const [isVesterDepositModalVisible, setIsVesterDepositModalVisible] = useState(false);
  const [vesterDepositTitle, setVesterDepositTitle] = useState("");
  const [vesterDepositStakeTokenLabel, setVesterDepositStakeTokenLabel] = useState("");
  const [vesterDepositMaxAmount, setVesterDepositMaxAmount] = useState("");
  const [vesterDepositBalance, setVesterDepositBalance] = useState("");
  const [vesterDepositEscrowedBalance, setVesterDepositEscrowedBalance] = useState("");
  const [vesterDepositAverageStakedAmount, setVesterDepositAverageStakedAmount] = useState("");
  const [vesterDepositMaxVestableAmount, setVesterDepositMaxVestableAmount] = useState("");
  const [vesterDepositValue, setVesterDepositValue] = useState("");
  const [vesterDepositReserveAmount, setVesterDepositReserveAmount] = useState("");
  const [vesterDepositMaxReserveAmount, setVesterDepositMaxReserveAmount] = useState("");
  const [vesterDepositAddress, setVesterDepositAddress] = useState("");

  const [isVesterWithdrawModalVisible, setIsVesterWithdrawModalVisible] = useState(false);
  const [vesterWithdrawTitle, setVesterWithdrawTitle] = useState(false);
  const [vesterWithdrawAddress, setVesterWithdrawAddress] = useState("");

  const [isCompoundModalVisible, setIsCompoundModalVisible] = useState(false);
  const [isClaimModalVisible, setIsClaimModalVisible] = useState(false);

  const rewardRouterAddress = getContract(chainId, "RewardRouter");
  const rewardReaderAddress = getContract(chainId, "RewardReader");
  const readerAddress = getContract(chainId, "Reader");

  const vaultAddress = getContract(chainId, "Vault");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");
  const equalAddress = getContract(chainId, "EQUAL");
  const esEqualAddress = getContract(chainId, "ES_EQUAL");
  const bnEqualAddress = getContract(chainId, "BN_EQUAL");
  const equityAddress = getContract(chainId, "EQUITY");

  const stakedEqualTrackerAddress = getContract(chainId, "StakedEqualTracker");
  const bonusEqualTrackerAddress = getContract(chainId, "BonusEqualTracker");
  const feeEqualTrackerAddress = getContract(chainId, "FeeEqualTracker");

  const stakedEquityTrackerAddress = getContract(chainId, "StakedEquityTracker");
  const feeEquityTrackerAddress = getContract(chainId, "FeeEquityTracker");

  const equityManagerAddress = getContract(chainId, "EquityManager");

  const stakedEqualDistributorAddress = getContract(chainId, "StakedEqualDistributor");
  const stakedEquityDistributorAddress = getContract(chainId, "StakedEquityDistributor");

  const equalVesterAddress = getContract(chainId, "EqualVester");
  const equityVesterAddress = getContract(chainId, "EquityVester");
  const equityTokenAddress = getContract(chainId, "EquityToken");

  const vesterAddresses = [equalVesterAddress, equityVesterAddress];

  const excludedEsEqualAccounts = [stakedEqualDistributorAddress, stakedEquityDistributorAddress];

  const nativeTokenSymbol = getConstant(chainId, "nativeTokenSymbol");
  const wrappedTokenSymbol = getConstant(chainId, "wrappedTokenSymbol");

  const walletTokens = [equalAddress, esEqualAddress, equityTokenAddress, equityAddress, stakedEqualTrackerAddress];
  const depositTokens = [
    equalAddress,
    esEqualAddress,
    stakedEqualTrackerAddress,
    bonusEqualTrackerAddress,
    bnEqualAddress,
    equityAddress,
  ];
  const rewardTrackersForDepositBalances = [
    stakedEqualTrackerAddress,
    stakedEqualTrackerAddress,
    bonusEqualTrackerAddress,
    feeEqualTrackerAddress,
    feeEqualTrackerAddress,
    feeEquityTrackerAddress,
  ];
  const rewardTrackersForStakingInfo = [
    stakedEqualTrackerAddress,
    bonusEqualTrackerAddress,
    feeEqualTrackerAddress,
    stakedEquityTrackerAddress,
    feeEquityTrackerAddress,
  ];

  const { data: walletBalances } = useSWR(
    [
      `StakeV2:walletBalances:${active}`,
      chainId,
      readerAddress,
      "getTokenBalancesWithSupplies",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: fetcher(library, Reader, [walletTokens]),
    }
  );

  const { data: depositBalances } = useSWR(
    [
      `StakeV2:depositBalances:${active}`,
      chainId,
      rewardReaderAddress,
      "getDepositBalances",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: fetcher(library, RewardReader, [depositTokens, rewardTrackersForDepositBalances]),
    }
  );

  const { data: stakingInfo } = useSWR(
    [`StakeV2:stakingInfo:${active}`, chainId, rewardReaderAddress, "getStakingInfo", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: fetcher(library, RewardReader, [rewardTrackersForStakingInfo]),
    }
  );

  const { data: stakedEqualSupply } = useSWR(
    [`StakeV2:stakedEqualSupply:${active}`, chainId, equalAddress, "balanceOf", stakedEqualTrackerAddress],
    {
      fetcher: fetcher(library, Token),
    }
  );

  const { data: aums } = useSWR([`StakeV2:getAums:${active}`, chainId, equityManagerAddress, "getAums"], {
    fetcher: fetcher(library, EquityManager),
  });

  const { data: nativeTokenPrice } = useSWR(
    [`StakeV2:nativeTokenPrice:${active}`, chainId, vaultAddress, "getMinPrice", nativeTokenAddress],
    {
      fetcher: fetcher(library, Vault),
    }
  );

  const { data: esEqualSupply } = useSWR(
    [`StakeV2:esEqualSupply:${active}`, chainId, readerAddress, "getTokenSupply", esEqualAddress],
    {
      fetcher: fetcher(library, Reader, [excludedEsEqualAccounts]),
    }
  );

  const { data: vestingInfo } = useSWR(
    [`StakeV2:vestingInfo:${active}`, chainId, readerAddress, "getVestingInfo", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: fetcher(library, Reader, [vesterAddresses]),
    }
  );

  const { equalPrice } = useEqualPrice(chainId, { fantom: chainId === FTM ? library : undefined }, active);

  let { total: totalEqualSupply } = useTotalEqualSupply();

  let { fantom: fantomEqualStaked, total: totalEqualStaked } = useTotalEqualStaked();
  let { total: excludedEqualSupply } = useTotalEqualExcludedFromSupply();

  let { total: equalSupply } = useTotalEqualSupply();

  const isEqualTransferEnabled = true;

  let esEqualSupplyUsd;
  if (esEqualSupply && equalPrice) {
    esEqualSupplyUsd = esEqualSupply.mul(equalPrice).div(expandDecimals(1, 18));
  }

  let aum;
  if (aums && aums.length > 0) {
    aum = aums[0].add(aums[1]).div(2);
  }

  const { balanceData, supplyData } = getBalanceAndSupplyData(walletBalances);
  const depositBalanceData = getDepositBalanceData(depositBalances);
  const stakingData = getStakingData(stakingInfo);
  const vestingData = getVestingData(vestingInfo);

  const processedData = getProcessedData(
    balanceData,
    supplyData,
    depositBalanceData,
    stakingData,
    vestingData,
    aum,
    nativeTokenPrice,
    stakedEqualSupply,
    equalPrice,
    equalSupply
  );

  let hasMultiplierPoints = false;
  let multiplierPointsAmount;
  if (processedData && processedData.bonusEqualTrackerRewards && processedData.bnEqualInFeeEqual) {
    multiplierPointsAmount = processedData.bonusEqualTrackerRewards.add(processedData.bnEqualInFeeEqual);
    if (multiplierPointsAmount.gt(0)) {
      hasMultiplierPoints = true;
    }
  }
  let totalRewardTokens;
  if (processedData && processedData.bnEqualInFeeEqual && processedData.bonusEqualInFeeEqual) {
    totalRewardTokens = processedData.bnEqualInFeeEqual.add(processedData.bonusEqualInFeeEqual);
  }

  let totalRewardTokensAndEquity;
  if (totalRewardTokens && processedData && processedData.equityBalance) {
    totalRewardTokensAndEquity = totalRewardTokens.add(processedData.equityBalance);
  }

  const bonusEqualInFeeEqual = processedData ? processedData.bonusEqualInFeeEqual : undefined;

  let stakedEqualSupplyUsd;
  if (!totalEqualStaked.isZero() && equalPrice) {
    stakedEqualSupplyUsd = totalEqualStaked.mul(equalPrice).div(expandDecimals(1, 18));
  }

  let totalSupplyUsd;
  if (totalEqualSupply && !totalEqualSupply.isZero() && excludedEqualSupply && !excludedEqualSupply.isZero() && equalPrice) {
    totalSupplyUsd = (totalEqualSupply.sub(excludedEqualSupply)).mul(equalPrice).div(expandDecimals(1, 18));
  }

  let maxUnstakeableEqual = bigNumberify(0);
  if (
    totalRewardTokens &&
    vestingData &&
    vestingData.equalVesterPairAmount &&
    multiplierPointsAmount &&
    processedData.bonusEqualInFeeEqual
  ) {
    const availableTokens = totalRewardTokens.sub(vestingData.equalVesterPairAmount);
    const stakedTokens = processedData.bonusEqualInFeeEqual;
    const divisor = multiplierPointsAmount.add(stakedTokens);
    if (divisor.gt(0)) {
      maxUnstakeableEqual = availableTokens.mul(stakedTokens).div(divisor);
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const showStakeEqualModal = () => {
    if (!isEqualTransferEnabled) {
      helperToast.error("EQUAL transfers not yet enabled");
      return;
    }

    setIsStakeModalVisible(true);
    setStakeModalTitle("Stake EQUAL");
    setStakeModalMaxAmount(processedData.equalBalance);
    setStakeValue("");
    setStakingTokenSymbol("EQUAL");
    setStakingTokenAddress(equalAddress);
    setStakingFarmAddress(stakedEqualTrackerAddress);
    setStakeMethodName("stakeEqual");
  };

  const showStakeEsEqualModal = () => {
    setIsStakeModalVisible(true);
    setStakeModalTitle("Stake esEQUAL");
    setStakeModalMaxAmount(processedData.esEqualBalance);
    setStakeValue("");
    setStakingTokenSymbol("esEQUAL");
    setStakingTokenAddress(esEqualAddress);
    setStakingFarmAddress(AddressZero);
    setStakeMethodName("stakeEsEqual");
  };

  const showEqualVesterDepositModal = () => {
    let remainingVestableAmount = vestingData.equalVester.maxVestableAmount.sub(vestingData.equalVester.vestedAmount);
    if (processedData.esEqualBalance.lt(remainingVestableAmount)) {
      remainingVestableAmount = processedData.esEqualBalance;
    }

    setIsVesterDepositModalVisible(true);
    setVesterDepositTitle("EQUAL Vault");
    setVesterDepositStakeTokenLabel("staked EQUAL + esEQUAL + Multiplier Points");
    setVesterDepositMaxAmount(remainingVestableAmount);
    setVesterDepositBalance(processedData.esEqualBalance);
    setVesterDepositEscrowedBalance(vestingData.equalVester.escrowedBalance);
    setVesterDepositMaxVestableAmount(vestingData.equalVester.maxVestableAmount);
    setVesterDepositAverageStakedAmount(vestingData.equalVester.averageStakedAmount);
    setVesterDepositReserveAmount(vestingData.equalVester.pairAmount);
    setVesterDepositMaxReserveAmount(totalRewardTokens);
    setVesterDepositValue("");
    setVesterDepositAddress(equalVesterAddress);
  };

  const showEquityVesterDepositModal = () => {
    let remainingVestableAmount = vestingData.equityVester.maxVestableAmount.sub(vestingData.equityVester.vestedAmount);
    if (processedData.esEqualBalance.lt(remainingVestableAmount)) {
      remainingVestableAmount = processedData.esEqualBalance;
    }

    setIsVesterDepositModalVisible(true);
    setVesterDepositTitle("EQUITY Vault");
    setVesterDepositStakeTokenLabel("staked EQUITY");
    setVesterDepositMaxAmount(remainingVestableAmount);
    setVesterDepositBalance(processedData.esEqualBalance);
    setVesterDepositEscrowedBalance(vestingData.equityVester.escrowedBalance);
    setVesterDepositMaxVestableAmount(vestingData.equityVester.maxVestableAmount);
    setVesterDepositAverageStakedAmount(vestingData.equityVester.averageStakedAmount);
    setVesterDepositReserveAmount(vestingData.equityVester.pairAmount);
    setVesterDepositMaxReserveAmount(processedData.equityBalance);
    setVesterDepositValue("");
    setVesterDepositAddress(equityVesterAddress);
  };

  const showEqualVesterWithdrawModal = () => {
    if (!vestingData || !vestingData.equalVesterVestedAmount || vestingData.equalVesterVestedAmount.eq(0)) {
      helperToast.error("You have not deposited any tokens for vesting.");
      return;
    }

    setIsVesterWithdrawModalVisible(true);
    setVesterWithdrawTitle("Withdraw from EQUAL Vault");
    setVesterWithdrawAddress(equalVesterAddress);
  };

  const showEquityVesterWithdrawModal = () => {
    if (!vestingData || !vestingData.equityVesterVestedAmount || vestingData.equityVesterVestedAmount.eq(0)) {
      helperToast.error("You have not deposited any tokens for vesting.");
      return;
    }

    setIsVesterWithdrawModalVisible(true);
    setVesterWithdrawTitle("Withdraw from EQUITY Vault");
    setVesterWithdrawAddress(equityVesterAddress);
  };

  const showUnstakeEqualModal = () => {
    if (!isEqualTransferEnabled) {
      helperToast.error("EQUAL transfers not yet enabled");
      return;
    }
    setIsUnstakeModalVisible(true);
    setUnstakeModalTitle("Unstake EQUAL");
    let maxAmount = processedData.equalInStakedEqual;
    if (
      processedData.equalInStakedEqual &&
      vestingData &&
      vestingData.equalVesterPairAmount.gt(0) &&
      maxUnstakeableEqual &&
      maxUnstakeableEqual.lt(processedData.equalInStakedEqual)
    ) {
      maxAmount = maxUnstakeableEqual;
    }
    setUnstakeModalMaxAmount(maxAmount);
    setUnstakeModalReservedAmount(vestingData.equalVesterPairAmount);
    setUnstakeValue("");
    setUnstakingTokenSymbol("EQUAL");
    setUnstakeMethodName("unstakeEqual");
  };

  const showUnstakeEsEqualModal = () => {
    setIsUnstakeModalVisible(true);
    setUnstakeModalTitle("Unstake esEQUAL");
    let maxAmount = processedData.esEqualInStakedEqual;
    if (
      processedData.esEqualInStakedEqual &&
      vestingData &&
      vestingData.equalVesterPairAmount.gt(0) &&
      maxUnstakeableEqual &&
      maxUnstakeableEqual.lt(processedData.esEqualInStakedEqual)
    ) {
      maxAmount = maxUnstakeableEqual;
    }
    setUnstakeModalMaxAmount(maxAmount);
    setUnstakeModalReservedAmount(vestingData.equalVesterPairAmount);
    setUnstakeValue("");
    setUnstakingTokenSymbol("esEQUAL");
    setUnstakeMethodName("unstakeEsEqual");
  };

  const renderMultiplierPointsLabel = useCallback(() => {
    return "Multiplier Points APR";
  }, []);

  const renderMultiplierPointsValue = useCallback(() => {
    return (
      <Tooltip
        handle={`100.00%`}
        position="right-bottom"
        renderContent={() => {
          return (
            <>
              Boost your rewards with Multiplier Points.&nbsp;
              <a
                href=" https://docs.equalizer.exchange/rewards#multiplier-points"
                rel="noreferrer"
                target="_blank"
                className="text-white"
              >
                More info
              </a>
              .
            </>
          );
        }}
      />
    );
  }, []);

  let earnMsg;
  if (totalRewardTokensAndEquity && totalRewardTokensAndEquity.gt(0)) {
    let equalAmountStr;
    if (processedData.equalInStakedEqual && processedData.equalInStakedEqual.gt(0)) {
      equalAmountStr = formatAmount(processedData.equalInStakedEqual, 18, 2, true) + " EQUAL";
    }
    let esEqualAmountStr;
    if (processedData.esEqualInStakedEqual && processedData.esEqualInStakedEqual.gt(0)) {
      esEqualAmountStr = formatAmount(processedData.esEqualInStakedEqual, 18, 2, true) + " esEQUAL";
    }
    let mpAmountStr;
    if (processedData.bonusEqualInFeeEqual && processedData.bnEqualInFeeEqual.gt(0)) {
      mpAmountStr = formatAmount(processedData.bnEqualInFeeEqual, 18, 2, true) + " MP";
    }
    let equityStr;
    if (processedData.equityBalance && processedData.equityBalance.gt(0)) {
      equityStr = formatAmount(processedData.equityBalance, 18, 2, true) + " EQUITY";
    }
    const amountStr = [equalAmountStr, esEqualAmountStr, mpAmountStr, equityStr].filter((s) => s).join(", ");
    earnMsg = (
      <div>
        You earn {nativeTokenSymbol} rewards with {formatAmount(totalRewardTokensAndEquity, 18, 2, true)} tokens.
        <br />
        Tokens: {amountStr}.
      </div>
    );
  }

  return (
    <div className="StakeV2 Page page-layout">
      <StakeModal
        isVisible={isStakeModalVisible}
        setIsVisible={setIsStakeModalVisible}
        chainId={chainId}
        title={stakeModalTitle}
        maxAmount={stakeModalMaxAmount}
        value={stakeValue}
        setValue={setStakeValue}
        active={active}
        account={account}
        library={library}
        stakingTokenSymbol={stakingTokenSymbol}
        stakingTokenAddress={stakingTokenAddress}
        farmAddress={stakingFarmAddress}
        rewardRouterAddress={rewardRouterAddress}
        stakeMethodName={stakeMethodName}
        hasMultiplierPoints={hasMultiplierPoints}
        setPendingTxns={setPendingTxns}
        nativeTokenSymbol={nativeTokenSymbol}
        wrappedTokenSymbol={wrappedTokenSymbol}
      />
      <UnstakeModal
        setPendingTxns={setPendingTxns}
        isVisible={isUnstakeModalVisible}
        setIsVisible={setIsUnstakeModalVisible}
        chainId={chainId}
        title={unstakeModalTitle}
        maxAmount={unstakeModalMaxAmount}
        reservedAmount={unstakeModalReservedAmount}
        value={unstakeValue}
        setValue={setUnstakeValue}
        library={library}
        unstakingTokenSymbol={unstakingTokenSymbol}
        rewardRouterAddress={rewardRouterAddress}
        unstakeMethodName={unstakeMethodName}
        multiplierPointsAmount={multiplierPointsAmount}
        bonusEqualInFeeEqual={bonusEqualInFeeEqual}
      />
      <VesterDepositModal
        isVisible={isVesterDepositModalVisible}
        setIsVisible={setIsVesterDepositModalVisible}
        chainId={chainId}
        title={vesterDepositTitle}
        stakeTokenLabel={vesterDepositStakeTokenLabel}
        maxAmount={vesterDepositMaxAmount}
        balance={vesterDepositBalance}
        escrowedBalance={vesterDepositEscrowedBalance}
        averageStakedAmount={vesterDepositAverageStakedAmount}
        maxVestableAmount={vesterDepositMaxVestableAmount}
        reserveAmount={vesterDepositReserveAmount}
        maxReserveAmount={vesterDepositMaxReserveAmount}
        value={vesterDepositValue}
        setValue={setVesterDepositValue}
        library={library}
        vesterAddress={vesterDepositAddress}
        setPendingTxns={setPendingTxns}
      />
      <VesterWithdrawModal
        isVisible={isVesterWithdrawModalVisible}
        setIsVisible={setIsVesterWithdrawModalVisible}
        vesterAddress={vesterWithdrawAddress}
        chainId={chainId}
        title={vesterWithdrawTitle}
        library={library}
        setPendingTxns={setPendingTxns}
      />
      <CompoundModal
        active={active}
        account={account}
        setPendingTxns={setPendingTxns}
        isVisible={isCompoundModalVisible}
        setIsVisible={setIsCompoundModalVisible}
        rewardRouterAddress={rewardRouterAddress}
        totalVesterRewards={processedData.totalVesterRewards}
        wrappedTokenSymbol={wrappedTokenSymbol}
        nativeTokenSymbol={nativeTokenSymbol}
        library={library}
        chainId={chainId}
      />
      <ClaimModal
        active={active}
        account={account}
        setPendingTxns={setPendingTxns}
        isVisible={isClaimModalVisible}
        setIsVisible={setIsClaimModalVisible}
        rewardRouterAddress={rewardRouterAddress}
        totalVesterRewards={processedData.totalVesterRewards}
        wrappedTokenSymbol={wrappedTokenSymbol}
        nativeTokenSymbol={nativeTokenSymbol}
        library={library}
        chainId={chainId}
      />
      <div className="Page-title-section mt-0">
        <div className="Page-title">Earn</div>
        <div className="Page-description">
          Stake{" "}
          <a href="https://docs.equalizer.exchange/equal/tokenomics" target="_blank" rel="noopener noreferrer">
            EQUAL
          </a>{" "}
          and{" "}
          <a href="https://docs.equalizer.exchange/equal/equity" target="_blank" rel="noopener noreferrer">
            EQUITY
          </a>{" "}
          to earn rewards.
        </div>
        {earnMsg && <div className="Page-description">{earnMsg}</div>}
      </div>
      <div className="StakeV2-content">
        <div className="StakeV2-cards">
          <div className="App-card StakeV2-gmx-card">
            <div className="App-card-title">EQUAL</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Price</div>
                <div>
                  {!equalPrice && "..."}
                  {equalPrice && (
                    <Tooltip
                      position="right-bottom"
                      className="nowrap"
                      handle={"$" + formatAmount(equalPrice, USD_DECIMALS, 2, true)}
                      renderContent={() => (
                        <>
                          Price on Fantom: ${formatAmount(equalPrice, USD_DECIMALS, 2, true)}
                        </>
                      )}
                    />
                  )}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Wallet</div>
                <div>
                  {formatKeyAmount(processedData, "equalBalance", 18, 2, true)} EQUAL ($
                  {formatKeyAmount(processedData, "equalBalanceUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Staked</div>
                <div>
                  {formatKeyAmount(processedData, "equalInStakedEqual", 18, 2, true)} EQUAL ($
                  {formatKeyAmount(processedData, "equalInStakedEqualUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">APR</div>
                <div>
                  <Tooltip
                    handle={`${formatKeyAmount(processedData, "equalAprTotalWithBoost", 2, 2, true)}%`}
                    position="right-bottom"
                    renderContent={() => {
                      return (
                        <>
                          <div className="Tooltip-row">
                            <span className="label">Escrowed EQUAL APR</span>
                            <span>{formatKeyAmount(processedData, "equalAprForEsEqual", 2, 2, true)}%</span>
                          </div>
                          {(!processedData.equalBoostAprForNativeToken ||
                            processedData.equalBoostAprForNativeToken.eq(0)) && (
                            <div className="Tooltip-row">
                              <span className="label">{nativeTokenSymbol} APR</span>
                              <span>{formatKeyAmount(processedData, "equalAprForNativeToken", 2, 2, true)}%</span>
                            </div>
                          )}
                          {processedData.equalBoostAprForNativeToken && processedData.equalBoostAprForNativeToken.gt(0) && (
                            <div>
                              <br />
                              <div className="Tooltip-row">
                                <span className="label">{nativeTokenSymbol} Base APR</span>
                                <span>{formatKeyAmount(processedData, "equalAprForNativeToken", 2, 2, true)}%</span>
                              </div>
                              <div className="Tooltip-row">
                                <span className="label">{nativeTokenSymbol} Boosted APR</span>
                                <span>{formatKeyAmount(processedData, "equalBoostAprForNativeToken", 2, 2, true)}%</span>
                              </div>
                              <div className="Tooltip-row">
                                <span className="label">{nativeTokenSymbol} Total APR</span>
                                <span>
                                  {formatKeyAmount(processedData, "equalAprForNativeTokenWithBoost", 2, 2, true)}%
                                </span>
                              </div>
                              <br />
                              <div className="muted">The Boosted APR is from your staked Multiplier Points.</div>
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Rewards</div>
                <div>
                  <Tooltip
                    handle={`$${formatKeyAmount(processedData, "totalEqualRewardsUsd", USD_DECIMALS, 2, true)}`}
                    position="right-bottom"
                    renderContent={() => {
                      return (
                        <>
                          <div className="Tooltip-row">
                            <span className="label">
                              {nativeTokenSymbol} ({wrappedTokenSymbol})
                            </span>
                            <span>
                              {formatKeyAmount(processedData, "feeEqualTrackerRewards", 18, 4)} ($
                              {formatKeyAmount(processedData, "feeEqualTrackerRewardsUsd", USD_DECIMALS, 2, true)})
                            </span>
                          </div>
                          <div className="Tooltip-row">
                            <span className="label">Escrowed EQUAL</span>
                            <span>
                              {formatKeyAmount(processedData, "stakedEqualTrackerRewards", 18, 4)} ($
                              {formatKeyAmount(processedData, "stakedEqualTrackerRewardsUsd", USD_DECIMALS, 2, true)})
                            </span>
                          </div>
                        </>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">{renderMultiplierPointsLabel()}</div>
                <div>{renderMultiplierPointsValue()}</div>
              </div>
              <div className="App-card-row">
                <div className="label">Boost Percentage</div>
                <div>
                  <Tooltip
                    handle={`${formatAmount(processedData.boostBasisPoints, 2, 2, false)}%`}
                    position="right-bottom"
                    renderContent={() => {
                      return (
                        <>
                          You are earning {formatAmount(processedData.boostBasisPoints, 2, 2, false)}% more{" "}
                          {nativeTokenSymbol} rewards using {formatAmount(processedData.bnEqualInFeeEqual, 18, 4, 2, true)}{" "}
                          Staked Multiplier Points.
                          <br />
                          <br />
                          Use the "Compound" button to stake your Multiplier Points.
                        </>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">Total Staked</div>
                <div>
                  {!totalEqualStaked && "..."}
                  {totalEqualStaked && (
                    <Tooltip
                      position="right-bottom"
                      className="nowrap"
                      handle={
                        formatAmount(totalEqualStaked, 18, 0, true) +
                        " EQUAL" +
                        ` ($${formatAmount(stakedEqualSupplyUsd, USD_DECIMALS, 0, true)})`
                      }
                      renderContent={() => (
                        <>
                          Fantom: {formatAmount(fantomEqualStaked, 18, 0, true)} EQUAL
                        </>
                      )}
                    />
                  )}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Total Supply</div>
                {(!totalEqualSupply && !excludedEqualSupply) && "..."}
                {(totalEqualSupply && excludedEqualSupply) && (
                  <div>
                    {formatAmount(totalEqualSupply ? totalEqualSupply.sub(excludedEqualSupply) : 0, 18, 0, true)} EQUAL ($
                    {formatAmount(totalSupplyUsd, USD_DECIMALS, 0, true)})
                  </div>
                )}
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-options">
                <a className="App-button-option App-card-option" href="https://equalizer.exchange/swap">
                {/* <a className="App-button-option App-card-option" href="https://equalizer.exchange/#/swap?outputCurrency=0x3Fd3A0c85B70754eFc07aC9Ac0cbBDCe664865A6"> */}
                  Buy EQUAL
                </a>
                {active && (
                  <button className="App-button-option App-card-option" onClick={() => showStakeEqualModal()}>
                    Stake
                  </button>
                )}
                {active && (
                  <button className="App-button-option App-card-option" onClick={() => showUnstakeEqualModal()}>
                    Unstake
                  </button>
                )}
                {active && (
                  <Link className="App-button-option App-card-option" to="/begin_account_transfer">
                    Transfer Account
                  </Link>
                )}
              </div>
            </div>
          </div>
          <div className="App-card primary StakeV2-total-rewards-card">
            <div className="App-card-title">Total Rewards</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">
                  {nativeTokenSymbol} ({wrappedTokenSymbol})
                </div>
                <div>
                  {formatKeyAmount(processedData, "totalNativeTokenRewards", 18, 4, true)} ($
                  {formatKeyAmount(processedData, "totalNativeTokenRewardsUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">EQUAL</div>
                <div>
                  {formatKeyAmount(processedData, "totalVesterRewards", 18, 4, true)} ($
                  {formatKeyAmount(processedData, "totalVesterRewardsUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Escrowed EQUAL</div>
                <div>
                  {formatKeyAmount(processedData, "totalEsEqualRewards", 18, 4, true)} ($
                  {formatKeyAmount(processedData, "totalEsEqualRewardsUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Multiplier Points</div>
                <div>{formatKeyAmount(processedData, "bonusEqualTrackerRewards", 18, 4, true)}</div>
              </div>
              <div className="App-card-row">
                <div className="label">Staked Multiplier Points</div>
                <div>{formatKeyAmount(processedData, "bnEqualInFeeEqual", 18, 4, true)}</div>
              </div>
              <div className="App-card-row">
                <div className="label">Total</div>
                <div>${formatKeyAmount(processedData, "totalRewardsUsd", USD_DECIMALS, 2, true)}</div>
              </div>
              <div className="App-card-bottom-placeholder">
                <div className="App-card-divider"></div>
                <div className="App-card-options">
                  {active && <button className="App-button-option App-card-option">Compound</button>}
                  {active && <button className="App-button-option App-card-option">Claim</button>}
                  {!active && (
                    <button className="App-button-option App-card-option" onClick={() => connectWallet()}>
                      Connect Wallet
                    </button>
                  )}
                </div>
              </div>
              <div className="App-card-bottom">
                <div className="App-card-divider"></div>
                <div className="App-card-options">
                  {active && (
                    <button
                      className="App-button-option App-card-option"
                      onClick={() => setIsCompoundModalVisible(true)}
                    >
                      Compound
                    </button>
                  )}
                  {active && (
                    <button className="App-button-option App-card-option" onClick={() => setIsClaimModalVisible(true)}>
                      Claim
                    </button>
                  )}
                  {!active && (
                    <button className="App-button-option App-card-option" onClick={() => connectWallet()}>
                      Connect Wallet
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="App-card">
            <div className="App-card-title">EQUITY ({chainName})</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Price</div>
                <div>${formatKeyAmount(processedData, "equityPrice", USD_DECIMALS, 3, true)}</div>
              </div>
              <div className="App-card-row">
                <div className="label">Wallet</div>
                <div>
                  {formatKeyAmount(processedData, "equityBalance", EQUITY_DECIMALS, 2, true)} EQUITY ($
                  {formatKeyAmount(processedData, "equityBalanceUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Staked</div>
                <div>
                  {formatKeyAmount(processedData, "equityBalance", EQUITY_DECIMALS, 2, true)} EQUITY ($
                  {formatKeyAmount(processedData, "equityBalanceUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">APR</div>
                <div>
                  <Tooltip
                    handle={`${formatKeyAmount(processedData, "equityAprTotal", 2, 2, true)}%`}
                    position="right-bottom"
                    renderContent={() => {
                      return (
                        <>
                          <div className="Tooltip-row">
                            <span className="label">
                              {nativeTokenSymbol} ({wrappedTokenSymbol}) APR
                            </span>
                            <span>{formatKeyAmount(processedData, "equityAprForNativeToken", 2, 2, true)}%</span>
                          </div>
                          <div className="Tooltip-row">
                            <span className="label">Escrowed EQUAL APR</span>
                            <span>{formatKeyAmount(processedData, "equityAprForEsEqual", 2, 2, true)}%</span>
                          </div>
                        </>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Rewards</div>
                <div>
                  <Tooltip
                    handle={`$${formatKeyAmount(processedData, "totalEquityRewardsUsd", USD_DECIMALS, 2, true)}`}
                    position="right-bottom"
                    renderContent={() => {
                      return (
                        <>
                          <div className="Tooltip-row">
                            <span className="label">
                              {nativeTokenSymbol} ({wrappedTokenSymbol})
                            </span>
                            <span>
                              {formatKeyAmount(processedData, "feeEquityTrackerRewards", 18, 4)} ($
                              {formatKeyAmount(processedData, "feeEquityTrackerRewardsUsd", USD_DECIMALS, 2, true)})
                            </span>
                          </div>
                          <div className="Tooltip-row">
                            <span className="label">Escrowed EQUAL</span>
                            <span>
                              {formatKeyAmount(processedData, "stakedEquityTrackerRewards", 18, 4)} ($
                              {formatKeyAmount(processedData, "stakedEquityTrackerRewardsUsd", USD_DECIMALS, 2, true)})
                            </span>
                          </div>
                        </>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">Total Staked</div>
                <div>
                  {formatKeyAmount(processedData, "equitySupply", 18, 2, true)} EQUITY ($
                  {formatKeyAmount(processedData, "equitySupplyUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Total Supply</div>
                <div>
                  {formatKeyAmount(processedData, "equitySupply", 18, 2, true)} EQUITY ($
                  {formatKeyAmount(processedData, "equitySupplyUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-options">
                <Link className="App-button-option App-card-option" to="/buy_equity">
                  Buy EQUITY
                </Link>
                <Link className="App-button-option App-card-option" to="/buy_equity#redeem">
                  Sell EQUITY
                </Link>
                {/* {hasInsurance && (
                  <a
                    className="App-button-option App-card-option"
                    href="https://app.insurace.io/Insurance/Cart?id=124&referrer=545066382753150189457177837072918687520318754040"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Purchase Insurance
                  </a>
                )} */}
              </div>
            </div>
          </div>
          <div className="App-card">
            <div className="App-card-title">Escrowed EQUAL</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Price</div>
                <div>${formatAmount(equalPrice, USD_DECIMALS, 2, true)}</div>
              </div>
              <div className="App-card-row">
                <div className="label">Wallet</div>
                <div>
                  {formatKeyAmount(processedData, "esEqualBalance", 18, 2, true)} esEQUAL ($
                  {formatKeyAmount(processedData, "esEqualBalanceUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Staked</div>
                <div>
                  {formatKeyAmount(processedData, "esEqualInStakedEqual", 18, 2, true)} esEQUAL ($
                  {formatKeyAmount(processedData, "esEqualInStakedEqualUsd", USD_DECIMALS, 2, true)})
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">APR</div>
                <div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(processedData, "equalAprTotalWithBoost", 2, 2, true)}%`}
                      position="right-bottom"
                      renderContent={() => {
                        return (
                          <>
                            <div className="Tooltip-row">
                              <span className="label">
                                {nativeTokenSymbol} ({wrappedTokenSymbol}) Base APR
                              </span>
                              <span>{formatKeyAmount(processedData, "equalAprForNativeToken", 2, 2, true)}%</span>
                            </div>
                            {processedData.bnEqualInFeeEqual && processedData.bnEqualInFeeEqual.gt(0) && (
                              <div className="Tooltip-row">
                                <span className="label">
                                  {nativeTokenSymbol} ({wrappedTokenSymbol}) Boosted APR
                                </span>
                                <span>{formatKeyAmount(processedData, "equalBoostAprForNativeToken", 2, 2, true)}%</span>
                              </div>
                            )}
                            <div className="Tooltip-row">
                              <span className="label">Escrowed EQUAL APR</span>
                              <span>{formatKeyAmount(processedData, "equalAprForEsEqual", 2, 2, true)}%</span>
                            </div>
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">{renderMultiplierPointsLabel()}</div>
                <div>{renderMultiplierPointsValue()}</div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-row">
                <div className="label">Total Staked</div>
                <div>
                  {formatKeyAmount(processedData, "stakedEsEqualSupply", 18, 0, true)} esEQUAL ($
                  {formatKeyAmount(processedData, "stakedEsEqualSupplyUsd", USD_DECIMALS, 0, true)})
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Total Supply</div>
                <div>
                  {formatAmount(esEqualSupply, 18, 0, true)} esEQUAL (${formatAmount(esEqualSupplyUsd, USD_DECIMALS, 0, true)}
                  )
                </div>
              </div>
              <div className="App-card-divider"></div>
              <div className="App-card-options">
                {active && (
                  <button className="App-button-option App-card-option" onClick={() => showStakeEsEqualModal()}>
                    Stake
                  </button>
                )}
                {active && (
                  <button className="App-button-option App-card-option" onClick={() => showUnstakeEsEqualModal()}>
                    Unstake
                  </button>
                )}
                {!active && (
                  <button className="App-button-option App-card-option" onClick={() => connectWallet()}>
                    Connect Wallet
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="Page-title-section">
          <div className="Page-title">Vest</div>
          <div className="Page-description">
            Convert esEQUAL tokens to EQUAL tokens.
            <br />
            Please read the{" "}
            <a href="https://docs.equalizer.exchange/equal/rewards#vesting" target="_blank" rel="noopener noreferrer">
              vesting details
            </a>{" "}
            before using the vaults.
          </div>
        </div>
        <div>
          <div className="StakeV2-cards">
            <div className="App-card StakeV2-gmx-card">
              <div className="App-card-title">EQUAL Vault</div>
              <div className="App-card-divider"></div>
              <div className="App-card-content">
                <div className="App-card-row">
                  <div className="label">Staked Tokens</div>
                  <div>
                    <Tooltip
                      handle={formatAmount(totalRewardTokens, 18, 2, true)}
                      position="right-bottom"
                      renderContent={() => {
                        return (
                          <>
                            {formatAmount(processedData.equalInStakedEqual, 18, 2, true)} EQUAL
                            <br />
                            {formatAmount(processedData.esEqualInStakedEqual, 18, 2, true)} esEQUAL
                            <br />
                            {formatAmount(processedData.bnEqualInFeeEqual, 18, 2, true)} Multiplier Points
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">Reserved for Vesting</div>
                  <div>
                    {formatKeyAmount(vestingData, "equalVesterPairAmount", 18, 2, true)} /{" "}
                    {formatAmount(totalRewardTokens, 18, 2, true)}
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">Vesting Status</div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(vestingData, "equalVesterClaimSum", 18, 4, true)} / ${formatKeyAmount(
                        vestingData,
                        "equalVesterVestedAmount",
                        18,
                        4,
                        true
                      )}`}
                      position="right-bottom"
                      renderContent={() => {
                        return (
                          <>
                            {formatKeyAmount(vestingData, "equalVesterClaimSum", 18, 4, true)} tokens have been converted
                            to EQUAL from the&nbsp;
                            {formatKeyAmount(vestingData, "equalVesterVestedAmount", 18, 4, true)} esEQUAL deposited for
                            vesting.
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">Claimable</div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(vestingData, "equalVesterClaimable", 18, 4, true)} EQUAL`}
                      position="right-bottom"
                      renderContent={() =>
                        `${formatKeyAmount(
                          vestingData,
                          "equalVesterClaimable",
                          18,
                          4,
                          true
                        )} EQUAL tokens can be claimed, use the options under the Total Rewards section to claim them.`
                      }
                    />
                  </div>
                </div>
                <div className="App-card-divider"></div>
                <div className="App-card-options">
                  {!active && (
                    <button className="App-button-option App-card-option" onClick={() => connectWallet()}>
                      Connect Wallet
                    </button>
                  )}
                  {active && (
                    <button className="App-button-option App-card-option" onClick={() => showEqualVesterDepositModal()}>
                      Deposit
                    </button>
                  )}
                  {active && (
                    <button className="App-button-option App-card-option" onClick={() => showEqualVesterWithdrawModal()}>
                      Withdraw
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="App-card StakeV2-gmx-card">
              <div className="App-card-title">EQUITY Vault</div>
              <div className="App-card-divider"></div>
              <div className="App-card-content">
                <div className="App-card-row">
                  <div className="label">Staked Tokens</div>
                  <div>{formatAmount(processedData.equityBalance, 18, 2, true)} EQUITY</div>
                </div>
                <div className="App-card-row">
                  <div className="label">Reserved for Vesting</div>
                  <div>
                    {formatKeyAmount(vestingData, "equityVesterPairAmount", 18, 2, true)} /{" "}
                    {formatAmount(processedData.equityBalance, 18, 2, true)}
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">Vesting Status</div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(vestingData, "equityVesterClaimSum", 18, 4, true)} / ${formatKeyAmount(
                        vestingData,
                        "equityVesterVestedAmount",
                        18,
                        4,
                        true
                      )}`}
                      position="right-bottom"
                      renderContent={() => {
                        return (
                          <>
                            {formatKeyAmount(vestingData, "equityVesterClaimSum", 18, 4, true)} tokens have been converted
                            to EQUAL from the&nbsp;
                            {formatKeyAmount(vestingData, "equityVesterVestedAmount", 18, 4, true)} esEQUAL deposited for
                            vesting.
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="App-card-row">
                  <div className="label">Claimable</div>
                  <div>
                    <Tooltip
                      handle={`${formatKeyAmount(vestingData, "equityVesterClaimable", 18, 4, true)} EQUAL`}
                      position="right-bottom"
                      renderContent={() =>
                        `${formatKeyAmount(
                          vestingData,
                          "equityVesterClaimable",
                          18,
                          4,
                          true
                        )} EQUAL tokens can be claimed, use the options under the Total Rewards section to claim them.`
                      }
                    ></Tooltip>
                  </div>
                </div>
                <div className="App-card-divider"></div>
                <div className="App-card-options">
                  {!active && (
                    <button className="App-button-option App-card-option" onClick={() => connectWallet()}>
                      Connect Wallet
                    </button>
                  )}
                  {active && (
                    <button className="App-button-option App-card-option" onClick={() => showEquityVesterDepositModal()}>
                      Deposit
                    </button>
                  )}
                  {active && (
                    <button className="App-button-option App-card-option" onClick={() => showEquityVesterWithdrawModal()}>
                      Withdraw
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
      <Footer />
    </div>
  );
}
